import { useState } from "react";
import { useDataProvider, useNotify, useCanAccess, useStore } from "ra-core";
import {
  Download,
  Loader2,
  ShieldAlert,
  Sparkles,
  Building2,
  User,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ds/ui/card";
import { Button } from "@/components/ds/ui/button";
import { Switch } from "@/components/ds/ui/switch";
import { ContactImportButton } from "../contacts/ContactImportButton";
import { contactsExporter } from "./exporters";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase";
import { ContactCompanyImportButton } from "../contacts/ContactCompanyImportButton";
import { DealImportButton } from "../deals/DealImportButton";
import { ActivityImportButton } from "../activities/ActivityImportButton";

// ── Enrichment Toggle ──────────────────────────────────────
const EnrichmentToggle = () => {
  const [autoEnrich, setAutoEnrich] = useStore<boolean>(
    "crm.enrichment.autoOnImport",
    false,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          AI Enrichment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">Auto-enrich new records</p>
              {!autoEnrich && (
                <span className="text-xs text-muted-foreground">
                  (recommended off during bulk imports)
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              When ON, new companies and contacts are automatically queued for
              AI profile generation (Exa search + LLM). Turn OFF before
              importing large datasets to avoid burning API credits.
            </p>
          </div>
          <Switch checked={autoEnrich} onCheckedChange={setAutoEnrich} />
        </div>
      </CardContent>
    </Card>
  );
};

// ── Queue Panel ────────────────────────────────────────────
const EnrichmentQueuePanel = () => {
  const { data: stats } = useQuery({
    queryKey: ["enrichment-stats"],
    queryFn: async () => {
      const { data } = await supabase.from("enrichment_queue").select("status");
      const counts = { pending: 0, processing: 0, completed: 0, failed: 0 };
      (data ?? []).forEach(
        (r: any) => counts[r.status as keyof typeof counts]++,
      );
      return counts;
    },
    refetchInterval: 10000,
  });

  const { data: details } = useQuery({
    queryKey: ["enrichment-details"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("enrichment_queue")
        .select(
          "id, resource, record_id, status, attempts, last_error, created_at",
        )
        .in("status", ["pending", "failed"])
        .order("created_at", { ascending: false })
        .limit(50);

      if (!rows || rows.length === 0) return { pending: [], failed: [] };

      const companyIds = rows
        .filter((r) => r.resource === "companies")
        .map((r) => r.record_id);
      const contactIds = rows
        .filter((r) => r.resource === "contacts")
        .map((r) => r.record_id);

      const [companiesRes, contactsRes] = await Promise.all([
        companyIds.length
          ? supabase.from("companies").select("id, name").in("id", companyIds)
          : Promise.resolve({ data: [] }),
        contactIds.length
          ? supabase
              .from("contacts")
              .select("id, first_name, last_name")
              .in("id", contactIds)
          : Promise.resolve({ data: [] }),
      ]);

      const nameMap: Record<string, string> = {};
      (companiesRes.data ?? []).forEach((c: any) => {
        nameMap[`companies:${c.id}`] = c.name || `Company #${c.id}`;
      });
      (contactsRes.data ?? []).forEach((c: any) => {
        const full = `${c.first_name || ""} ${c.last_name || ""}`.trim();
        nameMap[`contacts:${c.id}`] = full || `Contact #${c.id}`;
      });

      const enriched = rows.map((r) => ({
        ...r,
        name: nameMap[`${r.resource}:${r.record_id}`] ?? null,
      }));

      return {
        pending: enriched.filter(
          (r) => r.status === "pending" && r.name !== null,
        ),
        failed: enriched.filter(
          (r) => r.status === "failed" && r.name !== null,
        ),
      };
    },
    refetchInterval: 10000,
  });

  if (!stats) return null;

  const pendingCount = details?.pending.length ?? stats.pending;
  const failedCount = details?.failed.length ?? stats.failed;

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Enrichment Queue</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-4 text-sm">
          <span>Pending: {pendingCount}</span>
          <span>Processing: {stats.processing}</span>
          <span>Completed: {stats.completed}</span>
          <span className={failedCount > 0 ? "text-red-500 font-medium" : ""}>
            Failed: {failedCount}
          </span>
        </div>

        {details && details.pending.length > 0 && (
          <div className="text-xs text-muted-foreground border-t pt-3">
            <div className="font-medium mb-1.5 text-foreground">
              Processing next:
            </div>
            <ul className="space-y-0.5">
              {details.pending.slice(0, 3).map((item) => (
                <li key={item.id} className="flex items-center gap-1">
                  <span className="truncate max-w-md">{item.name}</span>
                  <span className="text-muted-foreground/60">
                    ({item.resource})
                  </span>
                </li>
              ))}
              {details.pending.length > 3 && (
                <li className="text-muted-foreground/70">
                  + {details.pending.length - 3} more...
                </li>
              )}
            </ul>
          </div>
        )}

        {details && details.failed.length > 0 && (
          <div className="text-xs border-t pt-3">
            <div className="font-medium mb-1.5 text-red-500">
              Failed items (will retry):
            </div>
            <ul className="space-y-1.5">
              {details.failed.slice(0, 3).map((item) => (
                <li key={item.id} className="flex flex-col gap-0.5">
                  <div className="flex items-start gap-1">
                    <span className="truncate max-w-md text-foreground">
                      {item.name}
                    </span>
                    <span className="text-muted-foreground/60 shrink-0">
                      ({item.resource}, {item.attempts}x)
                    </span>
                  </div>
                  {item.last_error && (
                    <div className="text-[10px] text-muted-foreground/80 ml-2 truncate max-w-lg">
                      {item.last_error}
                    </div>
                  )}
                </li>
              ))}
              {details.failed.length > 3 && (
                <li className="text-muted-foreground/70">
                  + {details.failed.length - 3} more...
                </li>
              )}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Standalone Export Button ───────────────────────────────
const StandaloneExportButton = ({
  resource,
  exporter,
  label = "Export CSV",
}: {
  resource: string;
  exporter?: any;
  label?: string;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { data } = await dataProvider.getList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter: {},
      });

      if (exporter) {
        const { fetchRelatedRecords } = await import("ra-core");
        await exporter(
          data,
          fetchRelatedRecords(dataProvider),
          dataProvider,
          resource,
        );
      } else {
        import("jsonexport/dist").then(({ default: jsonExport }) => {
          import("ra-core").then(({ downloadCSV }) => {
            jsonExport(data, {}, (_err: any, csv: string) => {
              downloadCSV(csv, resource);
            });
          });
        });
      }
    } catch (error: any) {
      notify(error.message || "Export failed", { type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={isExporting}
      className="gap-2"
    >
      {isExporting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      {isExporting ? "Exporting..." : label}
    </Button>
  );
};

// ── Main Page ──────────────────────────────────────────────
export const DataManagementPage = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "sales",
    action: "list",
  });

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center space-y-4">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h2 className="text-2xl font-bold">Access Denied</h2>
        <p className="text-muted-foreground">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Data Management</h1>
        <p className="text-muted-foreground">
          Import and export your CRM data in bulk.
        </p>
      </div>

      {/* 👇 Now actually rendered */}
      <EnrichmentToggle />
      <EnrichmentQueuePanel />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <Building2 className="h-4 w-4" />
            Contacts & Companies
          </CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <ContactCompanyImportButton />
          <StandaloneExportButton
            resource="contacts"
            exporter={contactsExporter}
          />
          <StandaloneExportButton resource="companies" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deals</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <DealImportButton />
          <StandaloneExportButton resource="deals" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activities</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <ActivityImportButton />
          <StandaloneExportButton resource="crm_activities" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <StandaloneExportButton resource="invoices" />
        </CardContent>
      </Card>
    </div>
  );
};

DataManagementPage.path = "/data-management";
